import React from 'react';
import { Loader2, Play } from 'lucide-react';
import { Modal } from '../../../../../../../components/ui/Modal';

interface VersaoItem {
  id: string;
  versao: number;
  titulo: string;
  descricao?: string;
  criadoEm: string;
  status: string;
  videoUrl?: string;
}

interface VersoesModalProps {
  open: boolean;
  onClose: () => void;
  versoesSubtopicoTitulo: string;
  loadingVersoes: boolean;
  versoesList: VersaoItem[];
  restoringVersao: number | null;
  handleRestoreVersao: (versaoNum: number) => Promise<void>;
}

export function VersoesModal({
  open,
  onClose,
  versoesSubtopicoTitulo,
  loadingVersoes,
  versoesList,
  restoringVersao,
  handleRestoreVersao,
}: VersoesModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Histórico de Versões — ${versoesSubtopicoTitulo}`}
      size="lg"
    >
      {loadingVersoes ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto 8px auto', color: 'var(--primary)' }} />
          <span>Carregando histórico...</span>
        </div>
      ) : versoesList.length === 0 ? (
        <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          Não há nenhuma gravação ou versão histórica registrada para esta aula no banco de dados.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '420px', overflowY: 'auto', paddingRight: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '.8rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                <th style={{ padding: '8px 12px' }}>Versão</th>
                <th style={{ padding: '8px 12px' }}>Título / Descrição</th>
                <th style={{ padding: '8px 12px' }}>Data</th>
                <th style={{ padding: '8px 12px' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {versoesList.map((v) => (
                <tr key={v.id} style={{ borderBottom: '1px solid rgba(226, 232, 240, 0.06)' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 700 }}>v{v.versao}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 600 }}>{v.titulo}</div>
                    {v.descricao && <div style={{ fontSize: '.7rem', color: 'var(--text-muted)' }}>{v.descricao}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {new Date(v.criadoEm).toLocaleString('pt-BR')}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      fontSize: '.65rem',
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: 4,
                      background: v.status === 'APROVADO' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                      color: v.status === 'APROVADO' ? '#10b981' : '#ef4444'
                    }}>
                      {v.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {v.videoUrl && (
                        <button
                          onClick={() => window.open(v.videoUrl, '_blank')}
                          className="action-btn"
                          style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}
                          title="Tocar vídeo desta versão"
                        >
                          <Play size={11} />
                        </button>
                      )}
                      <button
                        disabled={restoringVersao !== null}
                        onClick={() => handleRestoreVersao(v.versao)}
                        className="btn-primary"
                        style={{
                          fontSize: '.72rem',
                          padding: '3px 8px',
                          borderRadius: 4,
                          height: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                        title="Restaurar roteiro e mídia para esta versão"
                      >
                        {restoringVersao === v.versao ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <span>Restaurar</span>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
